import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IonButton,
  IonToggle,
  IonSkeletonText,
  IonCheckbox,
} from '@ionic/react';
import { notificationsOutline, mailOutline } from 'ionicons/icons';
import ProSectionHeader from '../../components/pro/ProSectionHeader';
import ProCard from '../../components/pro/ProCard';
import ProListRow from '../../components/pro/ProListRow';
import api from '../../lib/api';
import { useProToast } from './useProToast';
import { registerCurrentPushToken } from '../../hooks/useProPushNotifications';
import './ProAccount.css';

interface NotificationPreferences {
  bill_added_to_workspace?: boolean;
  calendar_event_created?: boolean;
  workspace_invitation?: boolean;
  note_created?: boolean;
  note_shared?: boolean;
  outreach_response_requested?: boolean;
  outreach_recipient_responded?: boolean;
  outreach_update?: boolean;
  system_announcements?: boolean;
}

interface WorkspaceRef {
  id: string;
  name: string;
}

// Server returns `selectedWorkspaces` as a list of `{ id, name }` rows
// (see backend/src/routes/calendar-email-subscriptions.js — both GET and
// PUT do `SELECT w.id, w.name FROM workspaces w ...`). The PUT *request*
// body uses bare ID strings (`workspaceIds: string[]`), so we project
// down to IDs in the mutation payload but keep the full ref shape in
// our cached data so optimistic updates round-trip cleanly with the
// server response.
interface CalendarSubscription {
  isSubscribed: boolean;
  includeAllWorkspaces: boolean;
  selectedWorkspaces: WorkspaceRef[];
  availableWorkspaces: WorkspaceRef[];
}

const NOTIFICATION_KEYS: Array<{
  key: keyof NotificationPreferences;
  title: string;
  note: string;
}> = [
  {
    key: 'bill_added_to_workspace',
    title: 'Bills added to workspaces',
    note: 'When a new bill is added to any workspace you belong to.',
  },
  {
    key: 'calendar_event_created',
    title: 'Calendar events',
    note: 'New calendar events for bills you track.',
  },
  {
    key: 'workspace_invitation',
    title: 'Workspace invitations',
    note: 'When someone invites you to collaborate in a workspace.',
  },
  {
    key: 'note_created',
    title: 'Workspace notes',
    note: 'When a note is added to one of your workspaces.',
  },
  {
    key: 'note_shared',
    title: 'Notes shared with you',
    note: 'When someone shares a note with you and requests feedback.',
  },
  {
    key: 'outreach_response_requested',
    title: 'Outreach responses requested',
    note: 'When an outreach report is shared with you requesting a response.',
  },
  {
    key: 'outreach_recipient_responded',
    title: 'Outreach recipient responses',
    note: 'When a recipient responds to outreach results you shared.',
  },
  {
    key: 'outreach_update',
    title: 'Outreach updates',
    note: 'When an organization posts an update on outreach you responded to.',
  },
  {
    key: 'system_announcements',
    title: 'System announcements',
    note: 'Operational updates from Capitol Commons, such as calendar delays.',
  },
];

type PushPermissionState = 'unknown' | 'granted' | 'denied' | 'prompt';

const ProProfileNotificationsPage: React.FC = () => {
  const [pendingPrefKey, setPendingPrefKey] = useState<keyof NotificationPreferences | null>(null);
  const [pushPermission, setPushPermission] = useState<PushPermissionState>('unknown');
  const isNative = Capacitor.isNativePlatform();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useProToast();

  useEffect(() => {
    if (!isNative) return;
    FirebaseMessaging.checkPermissions()
      .then((status) => {
        if (status.receive === 'granted') setPushPermission('granted');
        else if (status.receive === 'denied') setPushPermission('denied');
        else setPushPermission('prompt');
      })
      .catch(() => setPushPermission('unknown'));
  }, [isNative]);

  const onEnablePush = async () => {
    const status = await FirebaseMessaging.requestPermissions();
    if (status.receive === 'granted') {
      setPushPermission('granted');
      try {
        await registerCurrentPushToken();
      } catch (err) {
        // Token will also be registered on next app open via the hook's effect.
        console.error('Push token registration failed after permission grant:', err);
      }
      showSuccess('Push alerts enabled on this device.');
    } else {
      setPushPermission('denied');
      showError('Push permission was not granted. Enable alerts in system Settings.');
    }
  };

  const prefsQuery = useQuery<NotificationPreferences>(
    ['notification-preferences'],
    async () => {
      const { data } = await api.get('/notifications/preferences');
      return data?.data?.preferences ?? {};
    },
    { staleTime: 5 * 60 * 1000, retry: 1 }
  );

  const prefsMutation = useMutation(
    async (update: Partial<NotificationPreferences>) => {
      const { data } = await api.put('/notifications/preferences', update);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-preferences']);
        showSuccess('Preference updated.');
      },
      onError: (err: any) => {
        showError(err?.response?.data?.error || 'Could not update preference.');
      },
      onSettled: () => {
        setPendingPrefKey(null);
      },
    }
  );

  const calendarSubQuery = useQuery<CalendarSubscription>(
    ['calendar-subscription'],
    async () => {
      const { data } = await api.get('/calendar-email-subscriptions/my-subscription');
      return (
        data?.data ?? {
          isSubscribed: false,
          includeAllWorkspaces: true,
          selectedWorkspaces: [],
          availableWorkspaces: [],
        }
      );
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const subscribeMutation = useMutation(
    async (payload: { includeAllWorkspaces: boolean; workspaceIds: string[] }) => {
      const { data } = await api.post('/calendar-email-subscriptions/subscribe', payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['calendar-subscription']);
        showSuccess('Subscribed to calendar digest.');
      },
      onError: (err: any) => {
        showError(err?.response?.data?.error || 'Could not subscribe.');
      },
    }
  );

  const unsubscribeMutation = useMutation(
    async () => {
      const { data } = await api.post('/calendar-email-subscriptions/unsubscribe');
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['calendar-subscription']);
        showSuccess('Unsubscribed from calendar digest.');
      },
      onError: (err: any) => {
        showError(err?.response?.data?.error || 'Could not unsubscribe.');
      },
    }
  );

  const updatePreferencesMutation = useMutation(
    async (payload: { includeAllWorkspaces: boolean; workspaceIds: string[] }) => {
      const { data } = await api.put('/calendar-email-subscriptions/preferences', payload);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['calendar-subscription']);
      },
      onError: (err: any) => {
        showError(err?.response?.data?.error || 'Could not update digest preferences.');
      },
    }
  );

  const prefs = prefsQuery.data ?? {};
  const sub = calendarSubQuery.data;

  const onTogglePref = (key: keyof NotificationPreferences, value: boolean) => {
    setPendingPrefKey(key);
    prefsMutation.mutate({ [key]: value });
  };

  const onToggleDigest = (checked: boolean) => {
    if (checked) {
      subscribeMutation.mutate({
        includeAllWorkspaces: sub?.includeAllWorkspaces ?? true,
        // Server expects ID strings, not the full {id,name} refs we cache.
        workspaceIds: (sub?.selectedWorkspaces ?? []).map((w) => w.id),
      });
    } else {
      unsubscribeMutation.mutate();
    }
  };

  const onToggleIncludeAll = (checked: boolean) => {
    if (!sub) return;
    const nextSelections: WorkspaceRef[] = checked ? [] : sub.selectedWorkspaces;
    queryClient.setQueryData<CalendarSubscription>(['calendar-subscription'], {
      ...sub,
      includeAllWorkspaces: checked,
      selectedWorkspaces: nextSelections,
    });
    if (sub.isSubscribed) {
      updatePreferencesMutation.mutate({
        includeAllWorkspaces: checked,
        workspaceIds: nextSelections.map((w) => w.id),
      });
    }
  };

  const onToggleWorkspace = (workspaceId: string, checked: boolean) => {
    if (!sub) return;
    const workspace = sub.availableWorkspaces.find((w) => w.id === workspaceId);
    // Keep the cached optimistic update in the same {id,name} shape the
    // server returns, so the post-mutation refetch doesn't undo the
    // visual state when comparing references.
    const nextSelections: WorkspaceRef[] = checked
      ? workspace && !sub.selectedWorkspaces.some((w) => w.id === workspaceId)
        ? [...sub.selectedWorkspaces, workspace]
        : sub.selectedWorkspaces
      : sub.selectedWorkspaces.filter((w) => w.id !== workspaceId);
    queryClient.setQueryData<CalendarSubscription>(['calendar-subscription'], {
      ...sub,
      includeAllWorkspaces: false,
      selectedWorkspaces: nextSelections,
    });
    if (sub.isSubscribed) {
      updatePreferencesMutation.mutate({
        includeAllWorkspaces: false,
        workspaceIds: nextSelections.map((w) => w.id),
      });
    }
  };

  return (
    <>
      <div className="pro-account pb-8">
        {isNative && (
          <>
            <ProSectionHeader title="Push on this device" icon={notificationsOutline} />
            <div className="px-2">
              <ProCard>
                <ProListRow
                  title="Device push alerts"
                  note={
                    pushPermission === 'granted'
                      ? 'This device receives push for the alert types below.'
                      : pushPermission === 'denied'
                        ? 'Push is off in system Settings. Open Settings to allow notifications.'
                        : 'Get notified when bills, notes, or calendar events match your preferences.'
                  }
                  trailing={
                    pushPermission !== 'granted' ? (
                      <IonButton size="small" onClick={onEnablePush}>
                        Enable
                      </IonButton>
                    ) : undefined
                  }
                />
              </ProCard>
            </div>
          </>
        )}

        <ProSectionHeader title="In-app alerts" icon={notificationsOutline} />
        <div className="px-2">
          <ProCard>
            {prefsQuery.isLoading ? (
              <div className="p-4 space-y-3">
                <IonSkeletonText animated style={{ width: '70%', height: '14px' }} />
                <IonSkeletonText animated style={{ width: '60%', height: '14px' }} />
                <IonSkeletonText animated style={{ width: '75%', height: '14px' }} />
              </div>
            ) : (
              NOTIFICATION_KEYS.map(({ key, title, note }) => (
                <ProListRow
                  key={key}
                  title={title}
                  note={note}
                  trailing={
                    <IonToggle
                      checked={!!prefs[key]}
                      onIonChange={(e) => onTogglePref(key, e.detail.checked)}
                      disabled={pendingPrefKey === key}
                    />
                  }
                />
              ))
            )}
          </ProCard>
        </div>

        <ProSectionHeader title="Calendar email digest" icon={mailOutline} />
        <div className="px-2">
          <ProCard>
            {calendarSubQuery.isLoading || !sub ? (
              <div className="p-4">
                <IonSkeletonText animated style={{ width: '70%', height: '14px' }} />
              </div>
            ) : (
              <>
                <ProListRow
                  title="Daily digest email"
                  note="Get a daily email of upcoming tracked calendar events."
                  trailing={
                    <IonToggle
                      checked={sub.isSubscribed}
                      onIonChange={(e) => onToggleDigest(e.detail.checked)}
                      disabled={subscribeMutation.isLoading || unsubscribeMutation.isLoading}
                    />
                  }
                />

                {sub.isSubscribed && sub.availableWorkspaces.length > 1 && (
                  <ProListRow
                    title="Include all my workspaces"
                    note="Uncheck to pick specific workspaces."
                    trailing={
                      <IonCheckbox
                        checked={sub.includeAllWorkspaces}
                        onIonChange={(e) => onToggleIncludeAll(e.detail.checked)}
                      />
                    }
                  />
                )}

                {sub.isSubscribed &&
                  !sub.includeAllWorkspaces &&
                  sub.availableWorkspaces.length > 1 &&
                  sub.availableWorkspaces.map((w) => (
                    <ProListRow
                      key={w.id}
                      title={w.name}
                      trailing={
                        <IonCheckbox
                          checked={sub.selectedWorkspaces.some((sw) => sw.id === w.id)}
                          onIonChange={(e) => onToggleWorkspace(w.id, e.detail.checked)}
                        />
                      }
                    />
                  ))}
              </>
            )}
          </ProCard>
        </div>
      </div>
    </>
  );
};

export default ProProfileNotificationsPage;
